// src/components/Inv-Tracker/components/InvTrackerTree.jsx
// ============================================================================
// BLOCK ITR.CMP.001 — Inventory Tracker tree component
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders a three-level tree: item -> bin -> detail rows.
// - Supports select-all behavior at the item and bin levels.
// - Mirrors the Bank Posting left-tree interaction pattern.
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveIcon from '@mui/icons-material/Remove';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// ============================================================================
// BLOCK ITR.CMP.001.01 — Formatting helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Keep number / label formatting centralized.
// ============================================================================
function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatQuantity(value) {
  return toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function buildItemNodeKey(itemCode) {
  return `item::${String(itemCode ?? '').trim()}`;
}

function buildBinNodeKey(itemCode, binLocation) {
  return `bin::${String(itemCode ?? '').trim()}::${String(binLocation ?? '').trim()}`;
}

export function formatLotGroupLabel(group) {
  const lot = trimText(group?.lot_serial_no);
  const bin = trimText(group?.bin_location);
  const quantity = formatQuantity(group?.total_quantity ?? 0);

  if (lot && bin) {
    return `Lot: ${lot} • Bin: ${bin} • Qty ${quantity}`;
  }

  if (lot) {
    return `Lot: ${lot} • Qty ${quantity}`;
  }

  if (bin) {
    return `Bin: ${bin} • Qty ${quantity}`;
  }

  return `Qty ${quantity}`;
}

export function formatItemHeaderLabel(item) {
  const itemCode = trimText(item?.item_code);
  const quantity = formatQuantity(item?.total_quantity ?? 0);
  const lastTx = trimText(item?.last_transaction_code);

  if (itemCode && lastTx) {
    return `${itemCode} — Qty ${quantity} — Last Tx ${lastTx}`;
  }

  if (itemCode) {
    return `${itemCode} — Qty ${quantity}`;
  }

  return `Qty ${quantity}`;
}

function trimText(value) {
  return String(value ?? '').trim();
}

function getStatusChipProps(status) {
  if (status === 'cleared') {
    return {
      label: 'Cleared',
      color: 'success',
      icon: <CheckCircleOutlineIcon />,
    };
  }

  if (status === 'follow_up') {
    return {
      label: 'Follow Up',
      color: 'warning',
      icon: <ErrorOutlineIcon />,
    };
  }

  return {
    label: 'Open',
    color: 'default',
    icon: null,
  };
}

function computeSelectionState(sourceKeys, selectedRowMap) {
  const keys = (Array.isArray(sourceKeys) ? sourceKeys : []).filter(Boolean);
  const checkedCount = keys.filter((key) => Boolean(selectedRowMap[key])).length;

  return {
    checked: keys.length > 0 && checkedCount === keys.length,
    indeterminate: checkedCount > 0 && checkedCount < keys.length,
  };
}

function collectItemSourceKeys(item) {
  if (Array.isArray(item?.lot_groups) && item.lot_groups.length) {
    return item.lot_groups.flatMap((lot) => collectLotSourceKeys(lot));
  }

  return (Array.isArray(item?.bins) ? item.bins : []).flatMap((bin) => collectBinSourceKeys(bin));
}

function collectLotSourceKeys(lot) {
  return (Array.isArray(lot?.rows) ? lot.rows : []).flatMap((row) =>
    Array.isArray(row?.source_keys) ? row.source_keys : []
  );
}

function collectBinSourceKeys(bin) {
  return (Array.isArray(bin?.rows) ? bin.rows : []).flatMap((row) =>
    Array.isArray(row?.source_keys) ? row.source_keys : []
  );
}

function toggleManyKeys({ sourceKeys, checked, selectedRowMap, replaceSelectedKeys }) {
  const current = new Set(Object.keys(selectedRowMap).filter((key) => selectedRowMap[key]));

  sourceKeys.forEach((key) => {
    if (checked) {
      current.add(key);
    } else {
      current.delete(key);
    }
  });

  replaceSelectedKeys([...current]);
}

function DetailRow({ row, selectedRowMap, onToggleSelected }) {
  const statusChipProps = getStatusChipProps(row?.status);
  const checked = (Array.isArray(row?.source_keys) ? row.source_keys : []).every(
    (key) => selectedRowMap[key]
  );

  return (
    <Box sx={{ pl: 8, pr: 1, py: 0.5 }}>
      <ListItemButton sx={{ borderRadius: 1, minHeight: 44 }}>
        <Checkbox
          checked={checked}
          onChange={(event) => {
            (Array.isArray(row?.source_keys) ? row.source_keys : []).forEach((key) => {
              onToggleSelected(key, event.target.checked);
            });
          }}
        />

        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row?.detail_type === 'so_rollup'
                  ? `${row?.label} • ${row?.source_row_count} row(s)`
                  : `${row?.transaction_code || ''} • ${row?.transaction_date || ''}`}
              </Typography>

              <Chip size="small" {...statusChipProps} />
            </Stack>
          }
          secondary={
            <Stack spacing={0.2}>
              <Typography variant="caption">
                Bin: {row?.bin_location || '—'} • Lot: {row?.lot_serial_no || '—'} • Entered by:{' '}
                {row?.user_code || '—'} • Qty: {formatQuantity(row?.quantity)}
              </Typography>

              {!!row?.sales_order_no && (
                <Typography variant="caption">SO: {row.sales_order_no}</Typography>
              )}

              {!!row?.review_note && (
                <Typography variant="caption">Note: {row.review_note}</Typography>
              )}

              {(row?.cleared_by || row?.reviewed_by) && (
                <Typography variant="caption">
                  Reviewed by: {row?.reviewed_by || '—'}
                  {row?.cleared_by ? ` • Cleared by: ${row.cleared_by}` : ''}
                </Typography>
              )}
            </Stack>
          }
        />
      </ListItemButton>
    </Box>
  );
}

DetailRow.propTypes = {
  row: PropTypes.object.isRequired,
  selectedRowMap: PropTypes.object.isRequired,
  onToggleSelected: PropTypes.func.isRequired,
};

export default function InvTrackerTree({
  items,
  selectedRowMap,
  toggleSelectedKey,
  replaceSelectedKeys,
}) {
  const [openNodes, setOpenNodes] = React.useState({});

  React.useEffect(() => {
    const nextState = {};

    (Array.isArray(items) ? items : []).forEach((item) => {
      const itemKey = buildItemNodeKey(item?.item_code);
      nextState[itemKey] = openNodes[itemKey] ?? false;

      const groups =
        Array.isArray(item?.lot_groups) && item.lot_groups.length
          ? item.lot_groups
          : item?.bins || [];
      groups.forEach((group) => {
        const groupKey =
          group?.id ||
          buildBinNodeKey(item?.item_code, group?.bin_location || group?.lot_serial_no || 'group');
        nextState[groupKey] = openNodes[groupKey] ?? false;
      });

      (Array.isArray(item?.bins) ? item.bins : []).forEach((bin) => {
        const binKey = buildBinNodeKey(item?.item_code, bin?.bin_location);
        nextState[binKey] = openNodes[binKey] ?? false;
      });
    });

    setOpenNodes(nextState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  function toggleNode(nodeKey) {
    setOpenNodes((prev) => ({
      ...prev,
      [nodeKey]: !prev[nodeKey],
    }));
  }

  return (
    <List sx={{ width: '100%', p: 0 }}>
      {(Array.isArray(items) ? items : []).map((item) => {
        const itemKey = buildItemNodeKey(item?.item_code);
        const itemSourceKeys = collectItemSourceKeys(item);
        const itemSelectionState = computeSelectionState(itemSourceKeys, selectedRowMap);

        return (
          <Box key={itemKey}>
            <ListItemButton onClick={() => toggleNode(itemKey)} sx={{ minHeight: 48 }}>
              <Checkbox
                checked={itemSelectionState.checked}
                indeterminate={itemSelectionState.indeterminate}
                onChange={(event) => {
                  toggleManyKeys({
                    sourceKeys: itemSourceKeys,
                    checked: event.target.checked,
                    selectedRowMap,
                    replaceSelectedKeys,
                  });
                }}
              />

              {openNodes[itemKey] ? <RemoveIcon /> : <AddIcon />}

              <ListItemText
                sx={{ ml: 1 }}
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatItemHeaderLabel(item)}
                  </Typography>
                }
                secondary={
                  <Stack spacing={0.2}>
                    <Typography variant="caption">
                      {Number(item?.bin_count ?? 0)} bin(s) • {Number(item?.row_count ?? 0)} visible
                      row(s)
                    </Typography>
                    {!!item?.timestamp && (
                      <Typography variant="caption">Updated: {item.timestamp}</Typography>
                    )}
                  </Stack>
                }
              />
            </ListItemButton>

            <Collapse in={Boolean(openNodes[itemKey])} timeout="auto" unmountOnExit>
              <List sx={{ p: 0 }}>
                {(Array.isArray(item?.lot_groups) && item.lot_groups.length
                  ? item.lot_groups
                  : Array.isArray(item?.bins)
                    ? item.bins
                    : []
                ).map((group) => {
                  const groupKey =
                    group?.id ||
                    buildBinNodeKey(
                      item?.item_code,
                      group?.bin_location || group?.lot_serial_no || 'group'
                    );
                  const groupSourceKeys =
                    collectLotSourceKeys(group) || collectBinSourceKeys(group);
                  const groupSelectionState = computeSelectionState(
                    groupSourceKeys,
                    selectedRowMap
                  );
                  const label = formatLotGroupLabel(group);

                  return (
                    <Box key={groupKey}>
                      <ListItemButton
                        onClick={() => toggleNode(groupKey)}
                        sx={{ pl: 4, minHeight: 44 }}
                      >
                        <Checkbox
                          checked={groupSelectionState.checked}
                          indeterminate={groupSelectionState.indeterminate}
                          onChange={(event) => {
                            toggleManyKeys({
                              sourceKeys: groupSourceKeys,
                              checked: event.target.checked,
                              selectedRowMap,
                              replaceSelectedKeys,
                            });
                          }}
                        />

                        {openNodes[groupKey] ? <RemoveIcon /> : <AddIcon />}

                        <ListItemText
                          sx={{ ml: 1 }}
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {label} — Qty {formatQuantity(group?.total_quantity ?? 0)}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.2}>
                              {!!group?.affected_bins?.length && (
                                <Typography variant="caption">
                                  Bins: {group.affected_bins.join(', ')}
                                </Typography>
                              )}
                              {!!group?.timestamp && (
                                <Typography variant="caption">
                                  Timestamp: {group.timestamp}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                      </ListItemButton>

                      <Collapse in={Boolean(openNodes[groupKey])} timeout="auto" unmountOnExit>
                        {(Array.isArray(group?.rows) ? group.rows : []).map((row, rowIndex) => (
                          <React.Fragment key={row?.id || `${groupKey}::row::${rowIndex}`}>
                            <DetailRow
                              row={row}
                              selectedRowMap={selectedRowMap}
                              onToggleSelected={toggleSelectedKey}
                            />
                            <Divider component="li" sx={{ ml: 8 }} />
                          </React.Fragment>
                        ))}
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}

      {!items?.length && (
        <Box sx={{ p: 2 }}>
          <Tooltip title="Try another warehouse, reference date, or review filter.">
            <Typography variant="body2" color="text.secondary">
              No inventory review rows matched the current filters.
            </Typography>
          </Tooltip>
        </Box>
      )}
    </List>
  );
}

InvTrackerTree.propTypes = {
  items: PropTypes.array.isRequired,
  selectedRowMap: PropTypes.object.isRequired,
  toggleSelectedKey: PropTypes.func.isRequired,
  replaceSelectedKeys: PropTypes.func.isRequired,
};
